import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import {
  ShoppingCartIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const B2BDashboard = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    ordersThisMonth: 0,
    totalSpent: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    contractProducts: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch orders for the user (using max allowed limit of 100)
      // For accurate stats, we'll fetch multiple pages if needed
      let allOrders = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 10) { // Limit to 10 pages (1000 orders max)
        try {
          const response = await axios.get(`/api/orders/history/my-orders?page=${page}&limit=100`);
          const orders = response.data.orders || [];
          allOrders = [...allOrders, ...orders];
          
          // Check if there are more pages
          const total = response.data.pagination?.total || 0;
          const totalPages = response.data.pagination?.pages || 1;
          hasMore = page < totalPages && orders.length === 100;
          page++;
        } catch (pageError) {
          console.error(`Error fetching page ${page}:`, pageError);
          hasMore = false;
        }
      }
      
      // Calculate statistics
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const totalOrders = allOrders.length;
      const ordersThisMonth = allOrders.filter(order => {
        const orderDate = new Date(order.created_at || order.createdAt);
        return orderDate >= startOfMonth;
      }).length;
      
      const totalSpent = allOrders.reduce((sum, order) => {
        return sum + parseFloat(order.total_amount || order.total || 0);
      }, 0);
      
      const pendingOrders = allOrders.filter(order => 
        ['pending', 'processing', 'confirmed'].includes(order.status)
      ).length;
      
      const deliveredOrders = allOrders.filter(order => 
        ['delivered', 'completed'].includes(order.status)
      ).length;
      
      // Get recent orders (last 5)
      const sortedOrders = [...allOrders].sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt);
        const dateB = new Date(b.created_at || b.createdAt);
        return dateB - dateA;
      });
      setRecentOrders(sortedOrders.slice(0, 5));
      
      setStats({
        totalOrders,
        ordersThisMonth,
        totalSpent,
        pendingOrders,
        deliveredOrders,
        contractProducts: 0 // TODO: Implement contract products count
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Fallback: try to get at least first page
      try {
        const response = await axios.get('/api/orders/history/my-orders?limit=100');
        const orders = response.data.orders || [];
        setRecentOrders(orders.slice(0, 5));
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      name: 'הזמנה חדשה',
      description: 'צור הזמנת כמות',
      icon: ShoppingCartIcon,
      link: '/trade',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      name: 'ההזמנות שלי',
      description: 'צפה בהזמנות קודמות',
      icon: DocumentTextIcon,
      link: '/orders',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      name: 'תשלום מהיר',
      description: 'השלם הזמנה במהירות',
      icon: CurrencyDollarIcon,
      link: '/fast-checkout',
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      name: 'חבילות מיוחדות',
      description: 'עסקאות וחבילות',
      icon: ChartBarIcon,
      link: '/trade',
      color: 'bg-orange-500 hover:bg-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header with Logo */}
      <div className="bg-gradient-to-br from-green-50 to-white shadow-lg">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between md:gap-6">
            {/* Logo */}
            <div className="flex-shrink-0 mb-4 md:mb-0 flex justify-center md:justify-start">
              <img 
                src="/RamiCareLogo-02.png" 
                alt="Rami-Care Logo" 
                className="h-16 sm:h-20 md:h-24 w-auto object-contain"
              />
            </div>
            
            {/* Welcome Text */}
            <div className="flex-1 min-w-0 text-center md:text-right">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                שלום, {user?.company_name || user?.name || 'שותף עסקי'}
              </h1>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                ברוכים הבאים לפורטל העסקי של Rami-Care
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="mt-4 flex justify-center md:justify-end md:mt-0 md:mr-4">
              <Link
                to="/trade"
                className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 border border-transparent rounded-md shadow-sm text-sm sm:text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all transform hover:scale-105"
              >
                <ShoppingCartIcon className="-mr-1 ml-2 h-5 w-5" />
                הזמנה חדשה
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-xl transition-shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      סה"כ הזמנות
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-3xl font-bold text-gray-900">
                        {loading ? <span className="animate-pulse">...</span> : stats.totalOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-xl transition-shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      הזמנות החודש
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-3xl font-bold text-gray-900">
                        {loading ? <span className="animate-pulse">...</span> : stats.ordersThisMonth}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-xl transition-shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      סה"כ הוצאות
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-bold text-gray-900">
                        {loading ? <span className="animate-pulse">...</span> : `₪${stats.totalSpent.toLocaleString('he-IL')}`}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-xl transition-shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      הזמנות ממתינות
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-3xl font-bold text-gray-900">
                        {loading ? <span className="animate-pulse">...</span> : stats.pendingOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">פעולות מהירות</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {quickActions.map((action) => (
                <Link
                  key={action.name}
                  to={action.link}
                  className={`${action.color} text-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
                >
                  <action.icon className="h-8 w-8 mb-3" />
                  <h3 className="text-lg font-semibold mb-1">{action.name}</h3>
                  <p className="text-sm opacity-90">{action.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">הזמנות אחרונות</h2>
              <Link
                to="/orders"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                צפה בהכל →
              </Link>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-sm text-gray-500">טוען הזמנות...</p>
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">אין הזמנות</h3>
                <p className="mt-1 text-sm text-gray-500 mb-4">התחל בהזמנה חדשה</p>
                <Link
                  to="/trade"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <ShoppingCartIcon className="h-5 w-5 ml-2" />
                  התחל לקנות
                </Link>
              </div>
            ) : (
              <div className="overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {recentOrders.map((order) => (
                    <li key={order.id} className="py-4 hover:bg-gray-50 transition-colors">
                      <Link
                        to="/orders"
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <DocumentTextIcon className="h-8 w-8 text-primary-600" />
                          </div>
                          <div className="mr-4 flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                הזמנה #{order.order_number}
                              </p>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                order.status === 'delivered' || order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                order.status === 'processing' || order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status === 'pending' ? 'בהמתנה' :
                                 order.status === 'processing' ? 'בעיבוד' :
                                 order.status === 'confirmed' ? 'אושר' :
                                 order.status === 'shipped' ? 'נשלח' :
                                 order.status === 'delivered' ? 'נמסר' :
                                 order.status === 'completed' ? 'הושלם' :
                                 order.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {new Date(order.created_at || order.createdAt).toLocaleDateString('he-IL', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-left">
                            <p className="text-sm font-semibold text-gray-900">
                              ₪{parseFloat(order.total_amount || 0).toLocaleString('he-IL')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {order.items?.length || 0} פריטים
                            </p>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BDashboard;

